import { Project } from 'components/Project/types/project';
import React, {
  forwardRef, HTMLAttributes, useMemo, useRef,
} from 'react';
import { ReactComponent as RocketIcon } from 'assets/icons_3/rocket.svg';
import { ReactComponent as ChevronSelectorIcon } from 'assets/icons_3/arrows/chevron-selector-vertical.svg';
import { ReactComponent as PlusIcon } from 'assets/icons_3/plus.svg';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
// import CopyText from 'components/UI_kit_3/CopyText/CopyText';
import OverlayTrigger from 'components/UI_kit_3/Popover/OverlayTrigger/OverlayTrigger';
import styles from './ProjectDropdown.module.scss';

type SingleProps = {
  projectId?: number;
  onSelectProject: (id: number) => unknown;
  multiple?: false;
};

type MultipleProps = {
  projectId?: number[];
  onSelectProject: (id: number[]) => unknown;
  multiple: true;
};

type IProjectDropdownProps = HTMLAttributes<HTMLDivElement> & {
  projects: Project[];
  disabled?: boolean;
  frame?: boolean;
  error?: string | null;
  qaElement?: string;
  visibilityProjectId?: number;
  useOverlay?: boolean;
  overlayPosition?: 'bottom' | 'bottom-left' | 'bottom-right';
  hideCreateBtn?:boolean;
} & (SingleProps | MultipleProps);

const ProjectDropdown = forwardRef<HTMLButtonElement, IProjectDropdownProps>(
  (
    {
      projectId,
      frame,
      disabled,
      onSelectProject,
      className,
      error,
      multiple,
      placeholder,
      visibilityProjectId,
      qaElement,
      projects,
      useOverlay,
      overlayPosition = 'bottom-left',
      hideCreateBtn = false,
    },
    ref,
  ) => {
    const [t] = useTranslation();
    const [showProjects, setShowProjects] = React.useState(false);
    const projectsContainerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // const currentProject = useMemo(() => projects
    //   .find((project) => project.id === projectId), [projects, projectId]);
    // Замените блок currentProject useMemo на это:
    const selectedLabel = useMemo(() => {
      // Если ничего не выбрано
      if (!projectId || (Array.isArray(projectId) && projectId.length === 0)) {
        return placeholder;
      }

      // Логика для MULTIPLE
      if (multiple && Array.isArray(projectId)) {
        const selected = projects.filter((p) => projectId.includes(p.id));
        if (selected.length === 0) return placeholder;

        // Вариант А: Перечислить имена через запятую
        return selected.map((p) => p.name).join(', ');

        // Вариант Б: Если имен много, писать "Выбрано: N" (раскомментируйте, если нужно)
        // return `${t('selected')}: ${selected.length}`;
      }

      // Логика для SINGLE
      const project = projects.find((p) => p.id === projectId);
      return project?.name || placeholder;
    }, [projects, projectId, multiple, placeholder]);

    const selectedOwner = useMemo(() => {
      if (multiple) return null;
      const project = projects.find((p) => p.id === projectId);
      return project?.owner;
    }, [projects, projectId, multiple]);

    const handleClickOutside: EventListener = (e) => {
      const target = e.target as HTMLElement;
      if (menuRef.current && menuRef.current.contains(target)) {
        return;
      }
      if (projectsContainerRef.current && projectsContainerRef.current.contains(target)) {
        return;
      }
      setShowProjects(false);
    };

    const handleSelect = (value: number) => {
      if (multiple) {
        if (!projectId) {
          onSelectProject([value]);
          return;
        }
        if (projectId.includes(value)) {
          onSelectProject(projectId.filter((id) => id !== value));
          return;
        }
        onSelectProject([...projectId, value]);
        return;
      }
      onSelectProject(value);
      setShowProjects(false);
    };

    React.useEffect(() => {
      // if (!useOverlay) {
      if (showProjects) {
        window.addEventListener('click', handleClickOutside);
      } else {
        window.removeEventListener('click', handleClickOutside);
      }
      return () => {
        window.removeEventListener('click', handleClickOutside);
      };
      // }
      return () => {};
    }, [showProjects]);

    const triggerButton = (
      <button
        ref={ref}
        className={classNames(styles.toggle, {
          [styles.frame]: frame,
        })}
        type="button"
        qa-element="project-selector"
        disabled={disabled}
        onClick={() => setShowProjects(!showProjects)}
      >
        <RocketIcon />
        <span className={styles.projectName}>
          <span title={selectedLabel}>{selectedLabel}</span>
          {selectedOwner && <span className={styles.owner}>{selectedOwner}</span>}
        </span>
        <ChevronSelectorIcon className={styles.chevronSelector} />
      </button>
    );

    const menuContent = (
      <div
        className={classNames(styles.container, {
          [styles.menuOverlay]: useOverlay,
          [styles.menuOverlayVisible]: useOverlay && showProjects,
        })}
      >
        <div
          ref={menuRef}
          className={classNames(styles.menu, {
          })}
        >
          {projects.map((project) => (
            <button
              key={project.id}
              className={classNames(styles.item, {
                [styles.active]: multiple
                  ? projectId?.includes(project.id)
                  : projectId === project.id,
              })}
              type="button"
              disabled={
              (!multiple && projectId === project.id)
              || disabled
              || visibilityProjectId === project.id
            }
            // disabled={projectId === project.id || disabled || visibilityProjectId === project.id}
              onClick={() => handleSelect(project.id)}
              qa-element={`project-${project.id}`}
            >
              <span className={styles.projectName}>
                <span>{project?.name}</span>
                <span>{project?.owner}</span>
                {/* <span>
                <CopyText text={project?.id.toString() || ''} icon />
              </span> */}
              </span>
            </button>
          ))}
        </div>
        {!hideCreateBtn && (
          <>
            <hr className={styles.divider} />
            <Link
              to="/settings/projects"
              state={{ openCreateModal: true }}
              className={classNames(styles.item, styles.link)}
              onClick={() => setShowProjects(false)}
              qa-element="project-create-new"
            >
              <PlusIcon />
              {t('project.create_new_short')}
            </Link>
          </>
        )}
      </div>
    );

    if (useOverlay) {
      return (
        <div ref={projectsContainerRef} className={className} qa-element={qaElement}>
          <OverlayTrigger
            trigger="click"
            position={overlayPosition}
            overlay={menuContent}
            open={showProjects}
            onOpenChange={setShowProjects}
          >
            {triggerButton}
          </OverlayTrigger>
          {error && <div className={styles.error}>{error}</div>}
        </div>
      );
    }

    return (
      <div
        ref={projectsContainerRef}
        className={classNames(
          styles.menuWrapper,
          styles.projectsMenuWrapper,
          className,
          {
            [styles.show]: showProjects,
            [styles.invalid]: error,
          },
        )}
        qa-element={qaElement}
      >
        {triggerButton}
        {error && <div className={styles.error}>{error}</div>}
        {menuContent}
      </div>
    );
  },
);

export default ProjectDropdown;
